import { useState, useEffect, useRef, useCallback } from 'react';
import { userAPI } from '../services/api';

interface LocationData {
  latitude: number;
  longitude: number;
  address?: string;
  accuracy?: number;
  timestamp?: number;
}

interface UseCurrentLocationOptions {

  watch?: boolean;


  backendUpdateInterval?: number;


  minDistance?: number;


  enableHighAccuracy?: boolean;


  timeout?: number;


  maximumAge?: number;


  enableGeocoding?: boolean;


  enableBackendUpdates?: boolean;
}

interface UseCurrentLocationReturn {
  location: LocationData | null;
  error: GeolocationPositionError | null;
  isLoading: boolean;
  isWatching: boolean;
  startWatching: () => void;
  stopWatching: () => void;
  getCurrentLocation: () => Promise<LocationData | null>;
  refreshLocation: () => void;
}


export const useCurrentLocation = (
  options: UseCurrentLocationOptions = {}
): UseCurrentLocationReturn => {
  const {
    watch = true,
    backendUpdateInterval = 10000,
    minDistance = 10,
    enableHighAccuracy = true,
    timeout = 15000,
    maximumAge = 0,
    enableGeocoding = true,
    enableBackendUpdates = true,
  } = options;

  const [location, setLocation] = useState<LocationData | null>(null);
  const [error, setError] = useState<GeolocationPositionError | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isWatching, setIsWatching] = useState(false);

  const watchIdRef = useRef<number | null>(null);
  const lastBackendUpdateRef = useRef<number>(0);
  const lastLocationRef = useRef<LocationData | null>(null);
  const geocodeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backendUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);


  const reverseGeocode = useCallback(async (latitude: number, longitude: number): Promise<string | null> => {
    if (!enableGeocoding || !window.google?.maps?.Geocoder) {
      return null;
    }

    try {
      const geocoder = new window.google.maps.Geocoder();

      return new Promise((resolve) => {
        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results: any, status: string) => {
            if (status === 'OK' && results && results.length > 0) {

              const address = results[0].formatted_address || results[0].address_components?.map((c: any) => c.long_name).join(', ') || null;
              resolve(address);
            } else {
              console.warn('⚠️ Geocoding failed:', status);
              resolve(null);
            }
          }
        );
      });
    } catch (err) {
      console.error('❌ Geocoding error:', err);
      return null;
    }
  }, [enableGeocoding]);


  const calculateDistance = useCallback(
    (lat1: number, lon1: number, lat2: number, lon2: number): number => {
      const R = 6371000; 
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLon = ((lon2 - lon1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },
    []
  );


  const updateLocationToBackend = useCallback(
    async (locationData: LocationData) => {
      if (!enableBackendUpdates) return;

      try {
        await userAPI.updateLocation({
          latitude: locationData.latitude,
          longitude: locationData.longitude,
        });
        lastBackendUpdateRef.current = Date.now();
        console.log('✅ Location updated to backend:', locationData);
      } catch (err) {
        console.error('❌ Failed to update location to backend:', err);
      }
    },
    [enableBackendUpdates]
  );


  const processLocationUpdate = useCallback(
    async (position: GeolocationPosition, isManual = false) => {
      const { latitude, longitude, accuracy } = position.coords;
      const timestamp = position.timestamp || Date.now();


      const shouldUpdate =
        isManual ||
        !lastLocationRef.current ||
        calculateDistance(
          latitude,
          longitude,
          lastLocationRef.current.latitude,
          lastLocationRef.current.longitude
        ) >= minDistance;

      if (!shouldUpdate) {
        console.log('📍 Location not changed significantly, skipping update');
        return;
      }

      const locationData: LocationData = {
        latitude,
        longitude,
        accuracy,
        timestamp,
      };


      setLocation(locationData);
      setError(null);
      setIsLoading(false);
      lastLocationRef.current = locationData;


      if (enableGeocoding) {
        if (geocodeTimeoutRef.current) {
          clearTimeout(geocodeTimeoutRef.current);
        }
        geocodeTimeoutRef.current = setTimeout(async () => {
          const address = await reverseGeocode(latitude, longitude);
          if (address) {
            setLocation((prev) => (prev ? { ...prev, address } : null));
            if (lastLocationRef.current) {
              lastLocationRef.current = { ...lastLocationRef.current, address };
            }
          }
        }, 500);
      }


      const timeSinceLastUpdate = Date.now() - lastBackendUpdateRef.current;
      if (
        enableBackendUpdates &&
        (isManual || timeSinceLastUpdate >= backendUpdateInterval)
      ) {

        if (backendUpdateTimeoutRef.current) {
          clearTimeout(backendUpdateTimeoutRef.current);
        }


        backendUpdateTimeoutRef.current = setTimeout(() => {
          updateLocationToBackend(locationData);
        }, 1000);
      }
    },
    [
      minDistance,
      calculateDistance,
      enableGeocoding,
      reverseGeocode,
      enableBackendUpdates,
      backendUpdateInterval,
      updateLocationToBackend,
    ]
  );


  const getCurrentLocation = useCallback((): Promise<LocationData | null> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const err = new Error('Geolocation not supported') as any;
        err.code = 0;
        setError(err);
        reject(err);
        return;
      }

      setIsLoading(true);


      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      const adjustedTimeout = isMobile ? Math.max(timeout, 30000) : timeout;

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          await processLocationUpdate(position, true);
          resolve(lastLocationRef.current);
        },
        (err) => {
          console.error('❌ Geolocation error:', err);
          setError(err);
          setIsLoading(false);
          reject(err);
        },
        {
          enableHighAccuracy,
          timeout: adjustedTimeout,
          maximumAge,
        }
      );
    });
  }, [enableHighAccuracy, timeout, maximumAge, processLocationUpdate]);


  const startWatching = useCallback(() => {
    if (watchIdRef.current !== null) {
      console.log('⚠️ Already watching location');
      return;
    }

    if (!navigator.geolocation) {
      const err = new Error('Geolocation not supported') as any;
      err.code = 0;
      setError(err);
      return;
    }

    setIsLoading(true);
    setIsWatching(true);


    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    const adjustedTimeout = isMobile ? Math.max(timeout, 30000) : timeout;

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        await processLocationUpdate(position, false);
      },
      (err) => {
        console.error('❌ Geolocation watch error:', err);
        setError(err);
        setIsLoading(false);

      },
      {
        enableHighAccuracy,
        timeout: adjustedTimeout,
        maximumAge: maximumAge || 5000, 
      }
    );

    console.log('📍 Started watching location:', watchIdRef.current);
  }, [enableHighAccuracy, timeout, maximumAge, processLocationUpdate]);


  const stopWatching = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setIsWatching(false);
      console.log('📍 Stopped watching location');
    }
  }, []);


  const refreshLocation = useCallback(async () => {
    try {
      await getCurrentLocation();
    } catch (err) {
      console.error('❌ Failed to refresh location:', err);
    }
  }, [getCurrentLocation]);


  useEffect(() => {

    getCurrentLocation().catch((err) => {
      console.error('❌ Failed to get initial location:', err);
    });


    if (watch) {
      startWatching();
    }


    return () => {
      stopWatching();
      if (geocodeTimeoutRef.current) {
        clearTimeout(geocodeTimeoutRef.current);
      }
      if (backendUpdateTimeoutRef.current) {
        clearTimeout(backendUpdateTimeoutRef.current);
      }
    };
  }, [watch, startWatching, stopWatching, getCurrentLocation]);

  return {
    location,
    error,
    isLoading,
    isWatching,
    startWatching,
    stopWatching,
    getCurrentLocation,
    refreshLocation,
  };
};

